import React from 'react';
import { Button, Chip, Icon, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TaskListItemType, TaskListItemTypeTranslations, TaskListStatus } from '../../../../models/common';
import { AnimalType } from '../../../../models/animal';
import { DEFAULT_ROWS_PER_PAGE } from '../../../../constants/constants';
import {
  HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem,
  HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemVaccination
} from '../../../../api/HealthSystemApi';

interface IProps {
  plan: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem;
  item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemVaccination;
  expectedEndTime?: string | null;
}

const TaskPlanVaccinationRuleItem: React.FC<IProps> = (props) => {
  const { plan, item } = props;

  const { t } = useTranslation();

  const [expanded, setExpanded] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const rowsPerPage = DEFAULT_ROWS_PER_PAGE;
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const renderTaskListItemCardAction = (
    plan: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem,
    item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemVaccination
  ) => {
    switch (plan.status) {
      case TaskListStatus.Done:
        return (
          <Chip
            size="small"
            variant="outlined"
            className="!border-0 !bg-emerald-50 !text-emerald-700"
            icon={<Icon>{'check'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );

      case TaskListStatus.ToDo:
        return (
          <Chip
            size="small"
            variant="outlined"
            className="!border-0 !bg-amber-50 !text-amber-800"
            icon={<Icon>{'alarm'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );

      case TaskListStatus.InProgress:
        return (
          <Chip
            size="small"
            variant="outlined"
            icon={<Icon>{'hourglass_empty'}</Icon>}
            label={`${item.completedCount} / ${item.totalCount}`}
          />
        );
    }
  };

  const renderHeader = (item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemVaccination) => {
    const parts = [`${t('f-com-animal-type')}: ${AnimalType[item.animalType]}`];
    if (props.expectedEndTime) {
      parts.push(`${t('f-tasks-expected-end-time')}: ${props.expectedEndTime}`);
    }
    return parts.join(' | ');
  };

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
            <Icon className="!text-lg/none">
              {(item.type as unknown as TaskListItemType) === TaskListItemType.SickAnimals ? 'coronavirus' : 'vaccines'}
            </Icon>
          </span>
          <div>
            <div className="text-sm font-medium text-slate-800">
              {t(
                TaskListItemTypeTranslations[item.type as unknown as TaskListItemType] ??
                  String(TaskListItemType[item.type])
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">{renderHeader(item)}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {renderTaskListItemCardAction(plan, item)}
          {!!item.subtasks?.length && (
            <Button
              size="small"
              color="primary"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-controls={'vaccination-details-' + plan.id}
              endIcon={<Icon>{expanded ? 'expand_less' : 'expand_more'}</Icon>}
            >
              {expanded ? t('f-com-hide') : t('f-task-workspace-view-animals', 'View animals')}
            </Button>
          )}
        </div>
      </div>
      {expanded && item.subtasks && (
        <div id={'vaccination-details-' + plan.id} className="border-t border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-number')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-age')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-task-workspace-location', 'Location')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-task-workspace-disease', 'Disease')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-supply')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-dose')}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {item.subtasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((sub, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 font-medium">{sub.number}</td>
                    <td className="px-4 py-3">
                      {sub.ageDays != null ? t('f-com-age-days', { age: sub.ageDays }) : '—'}
                    </td>
                    <td className="px-4 py-3">{sub.location || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {sub.colour && (
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: sub.colour }} />
                        )}
                        {sub.disease || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{sub.medicalSupply || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {sub.completedDose.toFixed(2)} / {sub.dose?.toFixed(2) ?? '0'} CC
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          'inline-flex items-center gap-1 ' + (sub.completed ? 'text-emerald-700' : 'text-slate-500')
                        }
                      >
                        <Icon className="!text-base/none">{sub.completed ? 'check_circle_outline' : 'schedule'}</Icon>
                        {sub.completed ? t('f-task-workspace-completed', 'Completed') : t('f-tasks-lists-items-todo')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            component="div"
            rowsPerPageOptions={[DEFAULT_ROWS_PER_PAGE]}
            count={item.subtasks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </div>
      )}
    </div>
  );
};

export default TaskPlanVaccinationRuleItem;
